import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import request from '../helpers/request';
import { uploadFile, getPresignedUrl } from '../helpers/storage';
import complianceStructure from '../assets/policies/compliance.json';
import outputStructure from '../assets/policies/output.json';
import evaluationStructure from '../assets/policies/evaluation.json';
import {
  getChangedFields,
  cleanDocumentFieldsSchema,
  removeEmptyStrings,
  filterEmptyObjects
} from '../helpers/policyUtils';
import complianceV2Structure from '../assets/policies/compliance_v2.json';
import { useModels } from './useModels';

// Module-level cache to prevent duplicate API calls during TransitionGroup remounts
let cachedUsers = null;
let cachedFunctions = null;
let isFetchingData = false;

const isMissingModelValue = (value) => value === undefined || value === null || value === '';

export const getFallbackExtractionModelId = (models = []) => {
  if (!Array.isArray(models) || models.length === 0) return null;

  const activeModels = models.filter((model) => model?.is_active !== false);
  if (activeModels.length === 0) return null;

  const activeDefaultModel = activeModels.find((model) => model?.is_default === true);
  return activeDefaultModel?.id ?? activeModels[0]?.id ?? null;
};

export const backfillMissingDocumentFieldModels = (policy = {}, models = []) => {
  const documentFieldsSchema = policy?.policy?.documentFieldsSchema;
  if (!Array.isArray(documentFieldsSchema) || documentFieldsSchema.length === 0) {
    return {
      policy,
      missingCount: 0,
      wasBackfilled: false,
      isBlocked: false,
    };
  }

  const fallbackModelId = getFallbackExtractionModelId(models);
  let missingCount = 0;

  const updatedFields = documentFieldsSchema.map((field) => {
    if (!field || typeof field !== 'object') return field;
    if (!isMissingModelValue(field.model)) return field;

    missingCount += 1;
    if (fallbackModelId === null) return field;
    return {
      ...field,
      model: fallbackModelId,
    };
  });

  if (missingCount === 0) {
    return {
      policy,
      missingCount: 0,
      wasBackfilled: false,
      isBlocked: false,
    };
  }

  if (fallbackModelId === null) {
    return {
      policy,
      missingCount,
      wasBackfilled: false,
      isBlocked: true,
    };
  }

  return {
    policy: {
      ...policy,
      policy: {
        ...policy.policy,
        documentFieldsSchema: updatedFields,
      },
    },
    missingCount,
    wasBackfilled: true,
    isBlocked: false,
  };
};

export const isIntermediateSaveDisabled = ({
  isStepChanged = false,
  isNameChanged = false,
  isTemplatePrefillDraft = false,
} = {}) => !isStepChanged && !isNameChanged && !isTemplatePrefillDraft;

/**
 * Custom hook for PolicyWizard state management and API operations
 */
export const usePolicyWizard = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { id: routePolicyId } = useParams();
  const currentUserId = useSelector((state) => state.auth.user.id);
  const allPolicies = useSelector((state) => state.policies.policies);

  // Fetch models for model selection in extraction fields
  const { models, loading: isLoadingModels } = useModels();

  // Filter to get only compliance policies for linking to evaluation policies
  const compliancePolicies = useMemo(() =>
    allPolicies.filter(p => p.settings?.type?.toLowerCase() === 'compliance'),
    [allPolicies]
  );

  // Core policy state
  const [policyData, setPolicyData] = useState(null);
  const [currentPolicy, setCurrentPolicy] = useState({});
  const [currentStructure, setCurrentStructure] = useState({});

  // Navigation state
  const [stepIndex, setStepIndex] = useState(0);
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Loading states
  const [isLoading, setIsLoading] = useState(true);
  const [isTestLoading, setIsTestLoading] = useState(false);
  const [isGeneratePolicyLoading, setGeneratingPolicyLoading] = useState(false);
  const [isTextCropLoading, setTextCropLoading] = useState(false);

  // Data lists
  const [users, setUsers] = useState([]);
  const [functions, setFunctions] = useState([]);

  // Test/Preview state
  const [testPage, setTestingPage] = useState(null);
  const [testResultOpen, setTestResultOpen] = useState(false);
  const [isImageReferenceDrawerOpen, setImageReferenceDrawerOpen] = useState(false);

  // Modal states
  const [isGeneratedPolicyModal, setGeneratedPolicyModal] = useState(false);
  const [policyDefinition, setPolicyDefinition] = useState(null);
  const [isTextCropModal, setTextCropModal] = useState(false);
  const [cropBox, setCropBox] = useState(null);
  const [openFullscreenModal, setOpenFullscreenModal] = useState(false);

  // UI state
  const [isEditingName, setIsEditingName] = useState(false);

  // Evaluation policy state
  const [linkedCompliancePolicy, setLinkedCompliancePolicy] = useState(null);
  const [isUploadingDocument, setIsUploadingDocument] = useState(false);

  // Ref to prevent duplicate API calls (due to StrictMode + TransitionGroup)
  const hasFetchedData = useRef(false);
  // Track the policy ID to reset fetch guard when route changes
  const lastPolicyId = useRef(routePolicyId);
  // Prevent duplicate job template fetches
  const hasFetchedJobTemplate = useRef(false);
  // Prevent double-applying target step from auto-save navigation
  const hasAppliedTargetStep = useRef(false);
  // Keep latest policy snapshot for effects that should not re-run on every policy object mutation
  const currentPolicyRef = useRef(currentPolicy);

  // Structure types
  const structureTypes = useMemo(() => [
    { type: 'Compliance', structure: complianceStructure },
    { type: 'Compliance_v2', structure: complianceV2Structure },
    { type: 'Output', structure: outputStructure },
    { type: 'Evaluation', structure: evaluationStructure }
  ], []);

  // Computed values
  const isPolicyChanged = useMemo(() => {
    if (!routePolicyId) return false;
    if (!policyData || !currentPolicy) return false;

    const hasNameChanges = policyData.name !== currentPolicy.name;
    if (hasNameChanges) return true;

    const hasSettingsChanges = JSON.stringify(policyData) !== JSON.stringify(currentPolicy);
    if (hasSettingsChanges) return true;

    return false;
  }, [policyData, currentPolicy, routePolicyId]);

  const currentFieldKey = currentStructure?.fields?.[stepIndex]?.key;

  const isStepChanged = currentFieldKey
    ? JSON.stringify(policyData?.policy?.[currentFieldKey]) !== JSON.stringify(currentPolicy.policy?.[currentFieldKey])
    : false;

  const isNameChanged = policyData?.name !== currentPolicy?.name;
  const isTemplatePrefillDraft = useMemo(
    () => !routePolicyId && location.state?.isTemplatePrefill === true,
    [routePolicyId, location.state?.isTemplatePrefill]
  );
  const isIntermediateSaveButtonDisabled = isIntermediateSaveDisabled({
    isStepChanged,
    isNameChanged,
    isTemplatePrefillDraft,
  });

  const showImagePanel =
    currentPolicy.settings?.type?.toLowerCase() === "compliance" &&
    currentStructure?.fields?.[stepIndex]?.key === "documentFieldsSchema";

  const openImageReferenceDrawer = useCallback(() => {
    setImageReferenceDrawerOpen(true);
  }, []);

  const closeImageReferenceDrawer = useCallback(() => {
    setImageReferenceDrawerOpen(false);
  }, []);

  const clearTestingPage = useCallback(() => {
    setTestingPage((prev) => {
      if (prev?.url?.startsWith('blob:')) {
        URL.revokeObjectURL(prev.url);
      }
      return null;
    });
  }, []);

  // Fetch linked compliance policy for evaluation type
  const fetchLinkedCompliancePolicy = useCallback(async (policyId) => {
    if (!policyId) return;
    try {
      const response = await request.get(`/api/v1/compliance/policy/${policyId}/`);
      setLinkedCompliancePolicy(response.data);
    } catch (error) {
      console.error("Failed to fetch linked compliance policy:", error);
      toast.error("Failed to load linked compliance policy");
    }
  }, []);

  // Add document locally for evaluation policy (deferred upload on save)
  const addEvaluationDocument = useCallback((file) => {
    if (!file) return;

    // Create a blob URL for local preview
    const blobUrl = URL.createObjectURL(file);

    const newDocument = {
      id: `doc-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      name: file.name,
      url: blobUrl,
      pendingFile: file,  // Store the File object for later upload
      documentFields: {},
      validations: []
    };

    setCurrentPolicy(prev => ({
      ...prev,
      policy: {
        ...prev.policy,
        documents: [...(prev.policy?.documents || []), newDocument]
      }
    }));

    toast.success(`Document "${file.name}" added`);
  }, []);

  // Upload pending documents to S3 before saving (for evaluation policies)
  const uploadPendingDocuments = useCallback(async (documents, policyId) => {
    if (!documents || documents.length === 0) return documents;

    const uploadedDocuments = [];

    for (const doc of documents) {
      if (doc.pendingFile) {
        try {
          // Create a unique key for the document in S3
          const key = `evaluations/${policyId}/${doc.id}/${doc.name}`;

          // Upload the file to S3
          await uploadFile(doc.pendingFile, key);

          // Get the download URL for the uploaded file
          const { data } = await getPresignedUrl('download', key);

          // Revoke the blob URL to free memory
          if (doc.url && doc.url.startsWith('blob:')) {
            URL.revokeObjectURL(doc.url);
          }

          // Add the document with the S3 URL (without pendingFile)
          uploadedDocuments.push({
            ...doc,
            url: data.url,
            pendingFile: null,
            path: key, // Store the S3 key in path property
            s3Key: key  // Keep s3Key for backward compatibility if needed
          });
        } catch (error) {
          console.error(`Failed to upload document ${doc.name}:`, error);
          toast.error(`Failed to upload document "${doc.name}"`);
          // Keep the document with pendingFile so user can retry
          uploadedDocuments.push(doc);
        }
      } else {
        // Document already uploaded, keep as-is
        uploadedDocuments.push(doc);
      }
    }

    return uploadedDocuments;
  }, []);

  // Fetch initial data
  useEffect(() => {
    // Reset fetch guard when route changes (different policy ID or switching between create/edit)
    if (lastPolicyId.current !== routePolicyId) {
      hasFetchedData.current = false;
      lastPolicyId.current = routePolicyId;
    }

    // Guard against duplicate calls from StrictMode + TransitionGroup
    if (hasFetchedData.current) return;
    hasFetchedData.current = true;

    const fetchData = async () => {
      // Additional guard using module-level flag
      if (isFetchingData) return;
      isFetchingData = true;

      setIsLoading(true);

      try {
        // Use cached data if available, otherwise fetch
        if (cachedUsers && cachedFunctions) {
          setUsers(cachedUsers);
          setFunctions(cachedFunctions);
        } else {
          const usersResponse = await request.get('/api/v1/users/');
          cachedUsers = usersResponse.data;
          setUsers(usersResponse.data);

          const functionsResponse = await request.get('/api/v1/functions/');
          cachedFunctions = functionsResponse.data;
          setFunctions(functionsResponse.data);
        }
      } catch (error) {
        console.warn("Failed to load users or functions:", error);
      }

      try {
        // For new policy creation, check if policy data was passed via navigation state
        const statePolicy = location.state?.policy;

        if (!routePolicyId && !statePolicy) {
          console.warn("No policy ID or state policy found, redirecting to policies list");
          navigate('/app/policies');
          isFetchingData = false;
          return;
        }

        let loadedPolicy = null;
        if (routePolicyId) {
          const policyResponse = await request.get(`/api/v1/compliance/policy/${routePolicyId}/`);
          loadedPolicy = policyResponse.data;
        } else {
          // Creating new policy - use the policy from navigation state
          loadedPolicy = statePolicy;
        }

        // Hydrate documents with fresh URLs if needed
        if (loadedPolicy?.policy?.documents?.length > 0) {
          const hydratedDocuments = await Promise.all(loadedPolicy.policy.documents.map(async (doc) => {
            // Check if url is a path (not http/blob) or if we have a path property
            // If url is already a valid URL (http/blob), we might still want to refresh it if we have a path
            // But primarily we want to catch cases where url IS the path
            const isPathUrl = doc.url && !doc.url.startsWith('http') && !doc.url.startsWith('blob:');
            const hasPath = doc.path || doc.s3Key;

            if (isPathUrl || hasPath) {
              try {
                const key = doc.path || doc.s3Key || doc.url;
                // If the key looks like a path (e.g. contains /), try to sign it
                if (key && key.includes('/')) {
                  const { data } = await getPresignedUrl('download', key);
                  return {
                    ...doc,
                    url: data.url,
                    path: key // Ensure path is preserved
                  };
                }
              } catch (e) {
                console.warn(`Failed to refresh URL for document ${doc.name}`, e);
              }
            }
            return doc;
          }));

          loadedPolicy = {
            ...loadedPolicy,
            policy: {
              ...loadedPolicy.policy,
              documents: hydratedDocuments
            }
          };
        }

        setPolicyData(loadedPolicy);
        // We don't set currentPolicy here, it's set in the next useEffect based on policyData
        isFetchingData = false;
      } catch (error) {
        console.error("Error loading policy data:", error);
        toast.error("Failed to load policy data");
        navigate('/app/policies');
      } finally {
        setIsLoading(false);
        isFetchingData = false;
      }
    };

    fetchData();
  }, [routePolicyId, navigate, location.state?.policy]);

  // Initialize policy structure
  useEffect(() => {
    if (isLoading || !policyData || !policyData.name) return;

    // Case-insensitive comparison since API returns lowercase types
    const policyType = policyData.settings?.type?.toLowerCase();
    const structure = structureTypes.find(s => s.type.toLowerCase() === policyType);
    if (!structure) {
      console.warn("No matching structure found for policy type:", policyData.settings?.type);
      navigate('/app/policies');
      return;
    }

    let tempPolicy = { ...policyData };
    setCurrentStructure(structure.structure);

    structure.structure.fields.forEach(field => {
      // Special handling for flow field which comes nested in policy from backend
      // but we treat it as part of policy object in frontend state for editing
      if (field.key === 'flow' && tempPolicy.policy?.flow) {
        // It's already there from the API response
      } else if (!tempPolicy.policy?.[field.key]) {
        if (field.type === 'list' || field.type === 'evaluation-documents') {
          tempPolicy.policy[field.key] = [];
        } else if (field.type === 'flow-builder') {
          tempPolicy.policy[field.key] = field.default || { steps: [], config: {} };
        } else {
          tempPolicy.policy[field.key] = "";
        }
      }
    });

    structure.structure.settings.forEach(setting => {
      if (!tempPolicy.settings[setting.key]) {
        tempPolicy.settings[setting.key] = setting.default || "";
      }
    });

    if (!tempPolicy.users) {
      tempPolicy.users = [];
    }

    setCurrentPolicy(tempPolicy);
  }, [isLoading, policyData, structureTypes, navigate]);

  // Fetch linked compliance policy for evaluation type (case-insensitive since API returns lowercase)
  useEffect(() => {
    if (policyData?.settings?.type?.toLowerCase() === 'evaluation' && policyData?.settings?.linked_policy_id) {
      fetchLinkedCompliancePolicy(policyData.settings.linked_policy_id);
    }
  }, [policyData?.settings?.type, policyData?.settings?.linked_policy_id, fetchLinkedCompliancePolicy]);

  // Auto-assign current user on settings step
  useEffect(() => {
    if (stepIndex === currentStructure?.fields?.length && !routePolicyId && (!currentPolicy.users || currentPolicy.users.length === 0)) {
      if (currentUserId && users.some(user => user.id === parseInt(currentUserId))) {
        setCurrentPolicy(prev => ({
          ...prev,
          users: [parseInt(currentUserId)]
        }));
      }
    }
  }, [stepIndex, currentStructure?.fields?.length, routePolicyId, currentUserId, users, currentPolicy.users]);

  useEffect(() => {
    if (!showImagePanel) {
      setImageReferenceDrawerOpen(false);
    }
  }, [showImagePanel]);


  // Restore step after auto-save navigation (when no ID existed at job_configuration step)
  useEffect(() => {
    if (isLoading) return;
    if (hasAppliedTargetStep.current) return;
    if (location.state?.targetStepIndex === undefined) return;
    hasAppliedTargetStep.current = true;
    setStepIndex(location.state.targetStepIndex);
  }, [isLoading, location.state?.targetStepIndex]);

  useEffect(() => {
    currentPolicyRef.current = currentPolicy;
  }, [currentPolicy]);

  // Auto-fill job_configuration template when field is empty
  useEffect(() => {
    if (hasFetchedJobTemplate.current) return;

    const jobConfigField = currentStructure?.fields?.find(f => f.key === 'job_configuration');
    if (!jobConfigField) return;

    const jobConfigStepIndex = currentStructure.fields.indexOf(jobConfigField);
    if (stepIndex !== jobConfigStepIndex) return;

    if (currentPolicy?.policy?.job_configuration) return;

    const policyId = routePolicyId || currentPolicy?.id;

    if (!policyId) {
      // No ID yet — auto-save to create the policy, then come back to this step
      const autoSave = async () => {
        try {
          const policyToSave = { ...currentPolicyRef.current };
          if (policyToSave.policy?.flow) {
            policyToSave.flow = policyToSave.policy.flow;
            delete policyToSave.policy.flow;
          }
          const response = await request.post('/api/v1/compliance/policy/', policyToSave);
          const newId = response.data.id;
          setPolicyData(response.data);
          setCurrentPolicy(response.data);
          navigate(`/app/policies/wizard/${newId}`, {
            replace: true,
            state: { policy: response.data, targetStepIndex: jobConfigStepIndex }
          });
        } catch (err) {
          console.warn('Failed to auto-save for job template fetch:', err);
        }
      };
      autoSave();
      return;
    }

    hasFetchedJobTemplate.current = true;

    request.get(`/api/v1/compliance/policy/${policyId}/job-template/`)
      .then(response => {
        setCurrentPolicy(prev => ({
          ...prev,
          policy: {
            ...prev.policy,
            job_configuration: response.data
          }
        }));
      })
      .catch(err => {
        console.warn('Failed to fetch job config template:', err);
      });
  }, [stepIndex, currentStructure, routePolicyId, currentPolicy?.id, currentPolicy?.policy?.job_configuration, navigate]);

  // Navigation handlers
  const handleNext = useCallback(() => {
    setSelectedIndex(0);
    if (stepIndex < currentStructure?.fields?.length) setStepIndex(prev => prev + 1);
  }, [stepIndex, currentStructure]);

  const handleBack = useCallback(() => {
    setSelectedIndex(0);
    if (stepIndex > 0) setStepIndex(prev => prev - 1);
  }, [stepIndex]);

  // Clean policy for saving
  const cleanPolicyForSave = (policy) => {
    // Remove test results

    const secondFieldKey = currentStructure.fields[1]?.key;
    if (secondFieldKey && Array.isArray(policy.policy[secondFieldKey])) {
      policy.policy[secondFieldKey].forEach(item => {
        delete item.testResult;
      });
    }

    // Clean documents by removing pendingFile and setting url to path for storage
    const cleanedDocuments = policy.policy?.documents?.map(doc => {
      const { pendingFile, ...cleanDoc } = doc;
      // If we have a path/s3Key, ensure url is set to it for storage
      // This prevents storing expired signed URLs
      if (cleanDoc.path || cleanDoc.s3Key) {
        cleanDoc.url = cleanDoc.path || cleanDoc.s3Key;
      }
      return cleanDoc;
    });

    return {
      ...policy,
      policy: {
        ...policy.policy,
        documentFieldsSchema: cleanDocumentFieldsSchema(policy.policy.documentFieldsSchema),
        ReferencedObjects: filterEmptyObjects(removeEmptyStrings(policy.policy.ReferencedObjects)),
        validationConditions: filterEmptyObjects(removeEmptyStrings(policy.policy.validationConditions)),
        description: policy.policy.description,
        ...(cleanedDocuments && { documents: cleanedDocuments })
      }
    };
  };

  const ensureTemplateDraftModels = useCallback((policyToSave) => {
    if (!isTemplatePrefillDraft || routePolicyId) return policyToSave;

    const { policy: updatedPolicy, missingCount, isBlocked } = backfillMissingDocumentFieldModels(
      policyToSave,
      models
    );

    if (isBlocked) {
      const fieldLabel = missingCount === 1 ? 'field' : 'fields';
      const verb = missingCount === 1 ? 'is' : 'are';
      toast.error(
        `Cannot save template policy: ${missingCount} extraction ${fieldLabel} ${verb} missing model selection and no active model is configured.`
      );
      return null;
    }

    return updatedPolicy;
  }, [isTemplatePrefillDraft, routePolicyId, models]);

  // Save handlers
  const handleSave = async () => {
    try {
      let policyToSave = { ...currentPolicy };

      // For evaluation policies, upload any pending documents first
      const isEvaluation = currentPolicy?.settings?.type?.toLowerCase() === 'evaluation';
      const documents = currentPolicy?.policy?.documents || [];
      const hasPendingDocuments = documents.some(doc => doc.pendingFile);

      if (isEvaluation && hasPendingDocuments) {
        setIsUploadingDocument(true);
        toast.info("Uploading documents...");

        // Use existing policy ID or generate a temporary one for new policies
        const policyIdForUpload = routePolicyId || `new-${Date.now()}`;
        const uploadedDocuments = await uploadPendingDocuments(documents, policyIdForUpload);

        policyToSave = {
          ...policyToSave,
          policy: {
            ...policyToSave.policy,
            documents: uploadedDocuments
          }
        };

        setIsUploadingDocument(false);
      }

      const modelReadyPolicy = ensureTemplateDraftModels(policyToSave);
      if (!modelReadyPolicy) return;
      policyToSave = modelReadyPolicy;

      const cleanedPolicy = cleanPolicyForSave(policyToSave);

      let response;
      if (routePolicyId) {
        const changedFields = getChangedFields(policyData, cleanedPolicy);

        // Handle flow field asymmetry:
        if (changedFields.policy && changedFields.policy.flow) {
          changedFields.flow = changedFields.policy.flow;
          delete changedFields.policy.flow;
        }

        response = await request.patch(
          `/api/v1/compliance/policy/${routePolicyId}/`,
          changedFields
        );
        setPolicyData(response.data);
        setCurrentPolicy(response.data);
        setCurrentPolicy(response.data);
      } else {
        // Handle flow field asymmetry for create:
        // move policy.flow to top-level flow
        if (cleanedPolicy.policy && cleanedPolicy.policy.flow) {
          cleanedPolicy.flow = cleanedPolicy.policy.flow;
          delete cleanedPolicy.policy.flow;
        }

        response = await request.post("/api/v1/compliance/policy/", cleanedPolicy);
        const newId = response.data.id;
        setPolicyData(response.data);
        setCurrentPolicy(response.data);

        navigate(`/app/policies/wizard/${newId}`, {
          replace: true,
          state: { policy: response.data }
        });
      }

      toast.success("Your changes have been saved successfully.");
    } catch (e) {
      console.error("Saving policy: ", e);
      setIsUploadingDocument(false);

      if (e?.response?.status === 423) {
        toast.warn("This policy is locked and cannot be edited.");
        return;
      }

      toast.error("There was an error while saving the policy");
    }
  };

  const handleSaveOnLastStep = async () => {
    if (!currentPolicy?.settings?.tool) {
      toast.warn("No tool selected in policy settings. A default tool will be used when running tasks.");
    }

    try {
      let policyToSave = { ...currentPolicy };

      // For evaluation policies, upload any pending documents first
      const isEvaluation = currentPolicy?.settings?.type?.toLowerCase() === 'evaluation';
      const documents = currentPolicy?.policy?.documents || [];
      const hasPendingDocuments = documents.some(doc => doc.pendingFile);

      if (isEvaluation && hasPendingDocuments) {
        setIsUploadingDocument(true);
        toast.info("Uploading documents...");

        // Use existing policy ID or generate a temporary one for new policies
        const policyIdForUpload = routePolicyId || `new-${Date.now()}`;
        const uploadedDocuments = await uploadPendingDocuments(documents, policyIdForUpload);

        policyToSave = {
          ...policyToSave,
          policy: {
            ...policyToSave.policy,
            documents: uploadedDocuments
          }
        };

        setIsUploadingDocument(false);
      }

      const modelReadyPolicy = ensureTemplateDraftModels(policyToSave);
      if (!modelReadyPolicy) return;
      policyToSave = modelReadyPolicy;

      const cleanedPolicy = cleanPolicyForSave(policyToSave);

      let response;
      if (routePolicyId) {
        const changedFields = getChangedFields(policyData, cleanedPolicy);

        // Handle flow field asymmetry:
        if (changedFields.policy && changedFields.policy.flow) {
          changedFields.flow = changedFields.policy.flow;
          delete changedFields.policy.flow;
        }

        response = await request.patch(
          `/api/v1/compliance/policy/${routePolicyId}/`,
          changedFields
        );
        setPolicyData(response.data);
        setCurrentPolicy(response.data);
      } else {
        // Handle flow field asymmetry for create:
        if (cleanedPolicy.policy && cleanedPolicy.policy.flow) {
          cleanedPolicy.flow = cleanedPolicy.policy.flow;
          delete cleanedPolicy.policy.flow;
        }

        response = await request.post('/api/v1/compliance/policy/', cleanedPolicy);
        setPolicyData(response.data);
        setCurrentPolicy(response.data);
        navigate(`/app/policies/wizard/${response.data.id}`, {
          replace: true,
          state: { policy: response.data }
        });
      }

      toast.success("Your changes have been saved successfully.");
    } catch (e) {
      console.error("Saving policy: ", e);
      setIsUploadingDocument(false);

      if (e?.response?.status === 423) {
        toast.warn("This policy is locked and cannot be edited.");
        return;
      }

      toast.error("There was an error while saving the policy");
    }
  };

  // Update test result
  const handleUpdateItem = (id, updatedData) => {
    currentPolicy.policy[currentStructure.fields[stepIndex].key][id].testResult = updatedData;

    const updatedPolicy = {
      ...currentPolicy,
      policy: {
        ...currentPolicy.policy,
      }
    };
    setCurrentPolicy(updatedPolicy);
  };

  // File handling
  const handleFileChange = async (e) => {
    if (!e.target.files?.length) return;

    const file = e.target.files[0];
    const contents = URL.createObjectURL(file);

    setTestingPage((prev) => {
      if (prev?.url?.startsWith('blob:')) {
        URL.revokeObjectURL(prev.url);
      }

      return {
        name: file.name,
        file,
        url: contents,
      };
    });
  };

  // Test field
  const runTestField = async (listIndex) => {
    if (!testPage?.file) {
      toast.warn("Please upload a file before testing the field.");
      return;
    }

    if (!currentPolicy?.settings?.tool) {
      toast.warn("No tool selected in policy settings. A default tool will be used.");
    }

    let policyIdToSend = routePolicyId || currentPolicy.id;
    if (typeof policyIdToSend === 'undefined' || isNaN(Number(policyIdToSend))) {
      toast.warn("No valid policy ID yet: Running with the default compliance tool endpoint.");
    }

    setIsTestLoading(true);
    try {
      const field = currentPolicy.policy[currentStructure.fields[stepIndex].key][listIndex];
      delete field.testResult;

      if (field.type !== "date") {
        delete field.format;
      }
      if (field.type !== "json") {
        delete field.structure;
      }

      const formData = new FormData();
      formData.append("field", JSON.stringify(field));
      formData.append("file", testPage.file);
      formData.append("policy_id", policyIdToSend);

      const response = await request.post(
        "/api/v1/tools/test-field/",
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          },
        }
      );

      const answer = {
        total_run_time: response?.data.total_run_time || 0,
      };

      try {
        answer.answer = JSON.stringify(JSON.parse(Object.values(response?.data?.answer.documentFields)[0]), null, 2);
      } catch (e) {
        try {
          answer.answer = JSON.stringify(Object.values(response?.data?.answer.documentFields)[0], null, 2);
        } catch (e) {
          answer.answer = Object.values(response?.data?.answer.documentFields)[0];
        }
      }

      handleUpdateItem(listIndex, answer);
      setTestResultOpen(listIndex);
    } catch (err) {
      toast.error(`An error has occurred, ${err.message}`);
      console.error(err);
    }
    setIsTestLoading(false);
  };

  // Generate policy
  const generatePolicy = async (listIndex) => {
    if (!currentPolicy?.settings?.tool) {
      toast.warn("No tool selected in policy settings. A default tool will be used.");
    }

    let policyIdToSend = routePolicyId || currentPolicy.id;
    if (typeof policyIdToSend === 'undefined' || isNaN(Number(policyIdToSend))) {
      toast.warn("No valid policy ID yet: Running with the default compliance tool endpoint.");
    }

    setGeneratingPolicyLoading(true);
    try {
      const field = currentPolicy.policy[currentStructure.fields[stepIndex].key][listIndex];
      delete field.testResult;

      if (field.type !== "date") {
        delete field.format;
      }
      if (field.type !== "json") {
        delete field.structure;
      }

      const formData = new FormData();
      formData.append("field", JSON.stringify(field));
      formData.append("file", testPage.file);
      formData.append("policy_id", policyIdToSend);

      const response = await request.post(
        "/api/v1/compliance/policy/generate-policy/",
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          },
        }
      );

      setPolicyDefinition({ ...response.data, index: listIndex });
      setGeneratedPolicyModal(true);
    } catch (err) {
      toast.error(`An error has occurred, ${err.message}`);
      console.error(err);
    } finally {
      setGeneratingPolicyLoading(false);
    }
  };

  const applyPolicyDefinition = () => {
    const updatedPolicy = {
      ...currentPolicy,
      policy: {
        ...currentPolicy.policy,
        [currentStructure.fields[stepIndex].key]: [
          ...currentPolicy.policy[currentStructure.fields[stepIndex].key].slice(0, policyDefinition?.index),
          policyDefinition,
          ...currentPolicy.policy[currentStructure.fields[stepIndex].key].slice(policyDefinition?.index + 1)
        ]
      }
    };
    setCurrentPolicy(updatedPolicy);
    closeGeneratedPolicyModal();
  };

  const closeGeneratedPolicyModal = () => {
    setPolicyDefinition(null);
    setGeneratedPolicyModal(false);
  };

  // Text cropping
  const textCropping = async (listIndex) => {
    if (!currentPolicy?.settings?.tool) {
      toast.warn("No tool selected in policy settings. A default tool will be used.");
    }

    let policyIdToSend = routePolicyId || currentPolicy.id;
    if (typeof policyIdToSend === 'undefined' || isNaN(Number(policyIdToSend))) {
      toast.warn("No valid policy ID yet: Running with the default compliance tool endpoint.");
    }

    setTextCropLoading(true);
    try {
      const field = currentPolicy.policy[currentStructure.fields[stepIndex].key][listIndex];
      delete field.testResult;

      if (field.type !== "date") {
        delete field.format;
      }
      if (field.type !== "json") {
        delete field.structure;
      }

      const formData = new FormData();
      formData.append("text", field?.page_text);
      formData.append("file", testPage.file);
      formData.append("policy_id", policyIdToSend);

      const response = await request.post(
        "/api/v1/compliance/policy/text-cropping/",
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          },
        }
      );

      const { x0, x1, y0, y1 } = response.data;
      setTextCropModal(true);
      setCropBox({ x0, x1, y0, y1 });
    } catch (err) {
      toast.error(`An error has occurred, ${err.message}`);
      console.error(err);
    } finally {
      setTextCropLoading(false);
    }
  };

  const closeTextCroppingModal = () => {
    setTextCropModal(false);
  };

  // Fullscreen modal handlers
  const handleModalOpen = () => setOpenFullscreenModal(true);
  const handleModalClose = () => setOpenFullscreenModal(false);

  return {
    // Core state
    policyData,
    currentPolicy,
    setCurrentPolicy,
    currentStructure,
    routePolicyId,
    currentUserId,

    // Navigation
    stepIndex,
    setStepIndex,
    selectedIndex,
    setSelectedIndex,
    handleNext,
    handleBack,

    // Loading states
    isLoading,
    isTestLoading,
    isGeneratePolicyLoading,
    isTextCropLoading,

    // Data lists
    users,
    functions,
    models,
    isLoadingModels,

    // Computed values
    isPolicyChanged,
    isStepChanged,
    isNameChanged,
    isTemplatePrefillDraft,
    isIntermediateSaveButtonDisabled,
    showImagePanel,

    // Save handlers
    handleSave,
    handleSaveOnLastStep,
    getChangedFields: (original, updated) => getChangedFields(original, updated),

    // Test/Preview
    testPage,
    setTestingPage,
    clearTestingPage,
    testResultOpen,
    setTestResultOpen,
    runTestField,
    isImageReferenceDrawerOpen,
    openImageReferenceDrawer,
    closeImageReferenceDrawer,

    // Generate policy
    isGeneratedPolicyModal,
    policyDefinition,
    generatePolicy,
    applyPolicyDefinition,
    closeGeneratedPolicyModal,

    // Text cropping
    isTextCropModal,
    cropBox,
    textCropping,
    closeTextCroppingModal,

    // Fullscreen modal
    openFullscreenModal,
    handleModalOpen,
    handleModalClose,

    // File handling
    handleFileChange,

    // Evaluation policy
    linkedCompliancePolicy,
    isUploadingDocument,
    addEvaluationDocument,
    compliancePolicies,

    // UI state
    isEditingName,
    setIsEditingName,

    // Navigation
    navigate
  };
};

export default usePolicyWizard;
